import { useEffect, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Right slide-in canvas. Built from PollSphere's own surfaces — `erp-card`
 * borders, `h-12` header band matching the top bar, `text-erp-*` scale — because
 * PollSphere has no drawer component to port.
 *
 * It OVERLAYS rather than pushes. A push layout would reflow the grid columns
 * underneath, which loses the row the user just clicked — the one thing they are
 * looking at.
 *
 * Below 768px it becomes full-width: a 480px panel on a 390px phone is a
 * horizontal scrollbar.
 */
export function Drawer({
  open, onClose, title, subtitle, right, children, footer, width = 'md',
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  subtitle?: ReactNode
  right?: ReactNode
  children: ReactNode
  footer?: ReactNode
  width?: 'md' | 'lg' | 'xl'
}) {
  // Escape closes. Registered only while open, so a stack of drawers closes
  // top-down rather than all at once.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // The shell is `overflow: hidden` already; this stops the drawer's own
  // scroll chaining into the table behind it.
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        className="absolute inset-0 bg-black/30 backdrop-blur-[1px]"
        aria-label="Close"
        onClick={onClose}
      />
      <aside
        className={cn(
          'relative h-full bg-background border-l border-border shadow-xl flex flex-col',
          'w-full sm:w-[min(100vw,var(--drawer-w))]',
          // `xl` is for a drawer that carries its own tab strip and forms rather than
          // read-only fields. A third of the viewport: enough for a two-column form, and
          // it leaves the list it overlays readable — which is the whole reason detail is
          // a drawer and not a page. The floor keeps it usable on a laptop, where a third
          // of the width would be too narrow for a labelled select.
          width === 'xl' ? '[--drawer-w:max(420px,33vw)]'
            : width === 'lg' ? '[--drawer-w:640px]' : '[--drawer-w:480px]',
        )}
      >
        <header className="h-12 shrink-0 px-4 flex items-center gap-3 border-b border-border bg-muted/30">
          <div className="min-w-0 flex-1">
            <div className="text-erp-md font-semibold tracking-tight truncate">{title}</div>
            {subtitle && (
              <div className="text-erp-xs text-muted-foreground truncate -mt-0.5">{subtitle}</div>
            )}
          </div>
          {right}
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
            aria-label="Close panel"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto overscroll-contain">{children}</div>

        {footer && (
          <footer className="shrink-0 px-4 py-3 border-t border-border bg-muted/20 flex items-center justify-end gap-2">
            {footer}
          </footer>
        )}
      </aside>
    </div>
  )
}

/** Label/value row for drawer bodies. Long values wrap; they are never clipped. */
export function DrawerField({
  label, value, mono, className,
}: { label: string; value: ReactNode; mono?: boolean; className?: string }) {
  return (
    <div className={cn('grid grid-cols-[minmax(96px,34%)_1fr] gap-3 px-4 py-2 border-b border-border/60', className)}>
      <dt className="text-erp-xs text-muted-foreground pt-0.5">{label}</dt>
      <dd className={cn('text-erp-sm min-w-0 break-words', mono && 'font-mono text-erp-xs')}>
        {value ?? '—'}
      </dd>
    </div>
  )
}

export function DrawerSection({
  title, children, right,
}: { title: string; children: ReactNode; right?: ReactNode }) {
  return (
    <section>
      <h4 className="px-4 py-2 text-erp-xs font-semibold uppercase tracking-wide text-muted-foreground bg-muted/30 border-y border-border flex items-center justify-between gap-3">
        <span className="truncate">{title}</span>
        {/* Section-level actions live in the header, so an "Assess" or "Record"
            control sits with the data it changes rather than in a distant footer. */}
        {right && <span className="shrink-0 normal-case tracking-normal">{right}</span>}
      </h4>
      <dl>{children}</dl>
    </section>
  )
}
